import {
  Form,
  AvatarImage,
  Deadline,
  TaskContent,
  DropBlock,
  DotIcon,
  DropLabel,
  TaskItems,
  ButtonIcon,
  AssignGroup,
  MultipleLabel
} from './styles';
import { isEmpty, renderStatusIconName } from '../../../helpers/common';
import Avatar from '../../avatar';
import { TaskModel } from '../../../mobx/models/task';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';

import Linkify from 'react-linkify';
import { Label, MainTooltipDiv, TooltipDiv } from '../../tasks/styles';
import { DropdownButton } from '../../dropdown';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AVATAR_BORDER, LABEL_COLOUR_HASH, priorityList, TASK_PRIORITY } from '../../../global/constants';
import { StatusListInterface } from '../../../interfaces/ProjectInterface';
import Tooltip from '../../tooltip';
import { StatusDropdown } from '../../../pages/project/tasks/styles';
import CheckboxInput from '../../input/checkboxInput';

interface Props {
  task: TaskModel;
  currentProject: ProjectTemplateModel | null;
  loadData?: () => void;
  isView?: boolean;
}
const TemplateTaskItem: React.FC<Props> = ({ currentProject, loadData, task }) => {
  const [maxVisibleLabels, setMaxVisibleLabels] = useState(3);

  useEffect(() => {
    const updateLabelLimit = () => {
      setMaxVisibleLabels(window.innerWidth > 1024 ? 4 : 3);
    };

    updateLabelLimit(); // Initial check
    window.addEventListener('resize', updateLabelLimit);
    return () => window.removeEventListener('resize', updateLabelLimit);
  }, []);

  /**
   * @desc Gets priority details by key
   * @param key - Priority key to lookup
   */
  const getPriorityData = useCallback((key: number) => {
    return priorityList.find((item) => item.key === key);
  }, []);

  let priorityData = task?.priority;

  // Set default priority if none exists
  if (!priorityData && priorityData !== 0) {
    priorityData =
      priorityList?.find((item) => item.key === currentProject?.priority?.default)?.key || TASK_PRIORITY.NO_PRIORITY;
  }

  let statusDetails: StatusListInterface | undefined;
  if (currentProject?.statusEnable && currentProject?.statusData?.length > 0) {
    if (!task?.statusId) {
      statusDetails = currentProject?.defaultStatus;
    } else statusDetails = currentProject?.statusData?.find((x) => x['_id'] === task?.statusId);
  }

  const currentStatus =
    currentProject?.statusData?.find((x) => x['_id'] === task?.statusId) ||
    currentProject?.defaultStatus ||
    currentProject?.statusData?.[0];

  const taskLabels = useMemo(() => {
    if (!task?.labelsIds || !currentProject?.labelsList) return [];
    return task.labelsIds
      .map((labelId) => currentProject.labelsList.find((x) => x.id === labelId))
      .filter((label): label is (typeof currentProject.labelsList)[number] => !!label);
  }, [task.labelsIds, currentProject]);

  const renderSelectedLabel = useMemo(() => {
    if (taskLabels?.length === 0) return <></>;
    return (
      <>
        <MultipleLabel>
          {taskLabels.map((label, index) => {
            if (index < maxVisibleLabels) {
              return (
                <DropBlock key={label?.id} className={index === 0 ? 'left-border' : ''}>
                  <DotIcon color={LABEL_COLOUR_HASH[label?.color!]} />
                  <DropLabel>{label?.name}</DropLabel>
                </DropBlock>
              );
            } else if (index === maxVisibleLabels) {
              const remainingLabels = task.labels.length - maxVisibleLabels;
              if (remainingLabels > 0) {
                const tooltipContent = (
                  <MainTooltipDiv>
                    {task.labels.slice(maxVisibleLabels).map((l) => (
                      <TooltipDiv key={l.id}>
                        <DotIcon color={LABEL_COLOUR_HASH[l.color]} />
                        <span>{l.name}</span>
                      </TooltipDiv>
                    ))}
                  </MainTooltipDiv>
                );
                return (
                  <Tooltip title={tooltipContent} maxWidth={'max-content'} key={`more-${label?.id}`}>
                    <DropBlock>
                      <DropLabel>+{remainingLabels} more</DropLabel>
                    </DropBlock>
                  </Tooltip>
                );
              }
            }
            return null;
          })}
        </MultipleLabel>
      </>
    );
  }, [maxVisibleLabels, task.labels, taskLabels]);

  return (
    <TaskContent>
      <TaskItems>
        <Form>
          <StatusDropdown>
            {statusDetails && currentProject?.statusEnable && currentStatus ? (
              <ButtonIcon>
                <DropdownButton
                  iconName={renderStatusIconName(currentStatus?.Type)}
                  variant={'no-border'}
                  isActive={false}
                  size={'medium'}
                  isProjectTemplate={true}
                />
              </ButtonIcon>
            ) : (
              <ButtonIcon>
                <CheckboxInput
                  checked={task?.status}
                  onClick={(e) => e.stopPropagation()}
                  checkboxType='task-checkbox'
                />
              </ButtonIcon>
            )}

            <ButtonIcon>
              <DropdownButton
                iconName={getPriorityData(priorityData || TASK_PRIORITY.NO_PRIORITY)?.iconName}
                title={''}
                variant={'no-border'}
                size={'medium'}
                isProjectTemplate={true}
              />
            </ButtonIcon>
          </StatusDropdown>
          <Linkify
            componentDecorator={(decoratedHref: string, decoratedText: string, key: number) => (
              <a target='blank' href={decoratedHref} key={key}>
                {decoratedText}
              </a>
            )}>
            <Label isTemplateTaskPreview={true}>{task?.name}</Label>
          </Linkify>
        </Form>
        <AssignGroup className={'assign'}>
          <ul>
            {task?.users
              ?.filter((item) => !isEmpty(item?.id))
              ?.map((user) => {
                return (
                  <li key={user?.id}>
                    <AvatarImage>
                      <Avatar
                        imgSrc={user?.profile_photo || ''}
                        name={user?.name ? user.name : 'U N'}
                        size={20}
                        borderVariant={AVATAR_BORDER.THIN}
                      />
                    </AvatarImage>
                  </li>
                );
              })}
          </ul>
        </AssignGroup>
      </TaskItems>
      <Deadline>{renderSelectedLabel}</Deadline>
    </TaskContent>
  );
};

export default TemplateTaskItem;
